import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Spinner } from "./Spinner";

interface Props {
  children: ReactNode;
  require?: "client" | "admin";
}

/**
 * Routes that a suspended client is still allowed to visit. Anything else
 * gets redirected to /billing so they're funneled toward settling up.
 *   /billing  → settle outstanding invoice (the whole point)
 *   /profile  → update contact info (Stripe receipts go to client.email)
 */
const SUSPENDED_ALLOWED_PATHS = new Set(["/billing", "/profile"]);

export function ProtectedRoute({ children, require = "client" }: Props) {
  const { user, role, loading, suspended } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Spinner size="lg" fullScreen />;
  }

  if (!user || !role) {
    const target = require === "admin" ? "/admin/login" : "/login";
    return <Navigate to={target} state={{ from: location }} replace />;
  }

  if (require === "admin" && role !== "admin") {
    return <Navigate to="/" replace />;
  }
  if (require === "client" && role !== "client" && role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  // Suspended clients can sign in but can only see the billing flow.
  // Admins are unaffected by this gate.
  if (
    suspended &&
    require === "client" &&
    !SUSPENDED_ALLOWED_PATHS.has(location.pathname)
  ) {
    return <Navigate to="/billing" replace />;
  }

  return <>{children}</>;
}
